import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["preview", "form"]
  static values = {
    templateText: String,
    parameters: Array
  }

  connect() {
    this.updatePreview()
    // Manejar cambio de tipo de contacto (para templates con y sin parámetros)
    const contactTypeSelect = document.getElementById('contact_type_select')
    const contactTypeSelectNoParams = document.getElementById('contact_type_select_no_params')

    if (contactTypeSelect) {
      contactTypeSelect.addEventListener('change', (e) => {
        this.onContactTypeChange(e, '')
      })
    }
    if (contactTypeSelectNoParams) {
      contactTypeSelectNoParams.addEventListener('change', (e) => {
        this.onContactTypeChange(e, '_no_params')
      })
    }
  }

  onContactTypeChange(event, suffix = '') {
    const contactType = event.target.value
    const patientSelector = document.getElementById(`patient_selector${suffix}`)
    const supplierSelector = document.getElementById(`supplier_selector${suffix}`)
    const patientIdField = document.getElementById(`patient_id_field${suffix}`)
    const supplierIdField = document.getElementById(`supplier_id_field${suffix}`)

    if (contactType === 'patient') {
      if (patientSelector) patientSelector.style.display = ''
      if (supplierSelector) supplierSelector.style.display = 'none'
      if (patientIdField) patientIdField.required = true
      if (supplierIdField) supplierIdField.required = false
    } else if (contactType === 'supplier') {
      if (patientSelector) patientSelector.style.display = 'none'
      if (supplierSelector) supplierSelector.style.display = ''
      if (patientIdField) patientIdField.required = false
      if (supplierIdField) supplierIdField.required = true
    } else {
      if (patientSelector) patientSelector.style.display = 'none'
      if (supplierSelector) supplierSelector.style.display = 'none'
      if (patientIdField) patientIdField.required = false
      if (supplierIdField) supplierIdField.required = false
    }
  }

  // Actualizar preview cuando cambie cualquier campo
  updatePreview() {
    if (!this.templateTextValue) {
      return
    }

    let previewText = this.cleanTemplateText(this.templateTextValue)

    // Solo procesar parámetros si hay un formulario
    if (this.hasFormTarget) {
      // Obtener todos los valores del formulario
      const formData = new FormData(this.formTarget)
      const parameters = {}

      // Extraer parámetros del formulario y convertir IDs a nombres cuando sea necesario
      for (let [key, value] of formData.entries()) {
        if (key.startsWith('parameters[') && key.endsWith(']')) {
          const paramName = key.replace('parameters[', '').replace(']', '')

          // Para campos de select, obtener el texto visible en lugar del valor
          if (paramName === 'nombre' || paramName === 'nombre_doctor') {
            const selectElement = this.formTarget.querySelector(`select[name="parameters[${paramName}]"]`)
            if (selectElement && selectElement.options[selectElement.selectedIndex]) {
              parameters[paramName] = selectElement.options[selectElement.selectedIndex].text
            } else {
              parameters[paramName] = value
            }
          } else {
            parameters[paramName] = value
          }
        }
      }

      // Reemplazar variables en el template
      this.parametersValue.forEach(paramName => {
        let paramValue = parameters[paramName]

        // Si el valor está vacío o es solo espacios, usar placeholder
        if (!paramValue || paramValue.trim() === '') {
          paramValue = `{{${paramName}}}`
        }

        const regex = new RegExp(`{{${paramName}}}`, 'g')
        previewText = previewText.replace(regex, paramValue)
      })
    }

    // Actualizar el preview
    if (this.previewTarget) {
      // Buscar el elemento que contiene el texto del template (body)
      const bodyElement = this.previewTarget.querySelector('.template-body')
      // Use textContent to safely display text and prevent XSS attacks
      if (bodyElement) {
        bodyElement.textContent = previewText
      } else {
        // Si no existe el elemento body, actualizar todo el contenido
        this.previewTarget.textContent = previewText
      }
    } else {
      console.error("Preview target not found")
    }
  }

  // Formatear el preview usando la misma lógica que el helper de Rails
  formatPreview(text) {
    // 1. Remover escapes de caracteres especiales (como \$)
    let processed = text.replace(/\\(.)/g, '$1')

    // 2. Convertir markdown de WhatsApp a HTML
    // Solo procesar asteriscos para negrita, NO procesar guiones bajos
    // para evitar conflictos con variables como {{fecha_consulta}}

    // Primero procesar doble asterisco (títulos)
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Luego procesar asterisco simple (negrita)
    processed = processed.replace(/\*(.*?)\*/g, '<strong>$1</strong>')

    // 3. Asegurar que títulos en negrita estén en párrafos separados
    // Buscar patrones como <strong>Título</strong>\nTexto y agregar línea vacía
    processed = processed.replace(/(<strong>[^<]+<\/strong>)\n([^<\n])/g, '$1\n\n$2')

    // 4. Convertir saltos de línea en párrafos HTML
    // Dividir por doble salto de línea para crear párrafos
    const paragraphs = processed.split(/\n\s*\n/)
    const formattedParagraphs = paragraphs.map(para => {
      // Convertir saltos de línea simples a <br> dentro de cada párrafo
      const withBreaks = para.replace(/\n/g, '<br>')
      return `<p>${withBreaks}</p>`
    })

    return formattedParagraphs.join('')
  }

  // Limpiar el texto del template (remover \n extra y espacios)
  cleanTemplateText(text) {
    return text.replace(/\\n/g, '\n').trim()
  }

  // Eventos que disparan la actualización
  onInput(event) {
    this.updatePreview()
  }

  onChange(event) {
    this.updatePreview()
  }

  onDoctorChange(event) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]

    if (selectedOption && selectedOption.value) {
      // Obtener los datos del doctor seleccionado
      const firstPrice = selectedOption.dataset.firstPrice
      const followupPrice = selectedOption.dataset.followupPrice
      const address = selectedOption.dataset.address

      // Autocompletar campo precio_primera_vez si existe
      const precioFirstField = document.querySelector('input[name="parameters[precio_primera_vez]"]')
      if (precioFirstField && firstPrice) {
        precioFirstField.value = firstPrice
        precioFirstField.dispatchEvent(new Event('input', { bubbles: true }))
      }

      // Autocompletar campo precio_seguimiento si existe
      const precioFollowupField = document.querySelector('input[name="parameters[precio_seguimiento]"]')
      if (precioFollowupField && followupPrice) {
        precioFollowupField.value = followupPrice
        precioFollowupField.dispatchEvent(new Event('input', { bubbles: true }))
      }

      // Autocompletar campo direccion si existe
      const direccionField = document.querySelector('input[name="parameters[direccion]"]')
      if (direccionField && address) {
        direccionField.value = `La dirección del consultorio es ${address}.`
        direccionField.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }

    this.updatePreview()
  }

  validateBeforeSubmit(event) {
    if (!this.hasFormTarget) {
      return true
    }

    const form = this.formTarget
    const formData = new FormData(form)

    // Verificar si hay un campo link_de_onboarding
    const onboardingLinkField = form.querySelector('input[name="parameters[link_de_onboarding]"]')
    if (onboardingLinkField) {
      const onboardingLink = formData.get('parameters[link_de_onboarding]')

      // Validar que el link de onboarding no esté vacío
      if (!onboardingLink || onboardingLink.trim() === '') {
        event.preventDefault()
        event.stopPropagation()

        // Ocultar spinner si está visible (por si se activó antes de la validación)
        const loadingOverlay = document.querySelector('[data-loading-target="overlay"]')
        if (loadingOverlay) {
          loadingOverlay.classList.add('d-none')
        }

        // Mostrar mensaje de error
        onboardingLinkField.classList.add('is-invalid')
        const errorMessage = document.createElement('div')
        errorMessage.classList.add('invalid-feedback')
        errorMessage.textContent = 'El link de onboarding es obligatorio. Por favor, selecciona un paciente para generar el link automáticamente.'
        onboardingLinkField.parentElement.appendChild(errorMessage)

        // Remover el error después de que el usuario interactúe con el campo
        onboardingLinkField.addEventListener('input', function removeError() {
          onboardingLinkField.classList.remove('is-invalid')
          onboardingLinkField.setCustomValidity('')
          onboardingLinkField.removeEventListener('input', removeError)
        }, { once: true })

        return false
      }
    }

    // Validar otros campos requeridos
    const requiredFields = form.querySelectorAll('input[required], select[required], textarea[required]')
    let hasInvalidFields = false

    requiredFields.forEach(field => {
      if (!field.value || field.value.trim() === '') {
        field.classList.add('is-invalid')
        hasInvalidFields = true
        // Add error message to the field abajo del input
        const errorMessage = document.createElement('div')
        errorMessage.classList.add('invalid-feedback')
        errorMessage.textContent = 'Este campo es obligatorio.'
        field.parentElement.appendChild(errorMessage)
      } else {
        field.classList.remove('is-invalid')
        const errorMessage = field.parentElement.querySelector('.invalid-feedback')
        if (errorMessage) {
          errorMessage.remove()
        }
      }
    })

    if (hasInvalidFields) {
      event.preventDefault()
      event.stopPropagation()

      alert('Por favor, completa todos los campos obligatorios.')
      return false
    } else {
      // Si todos los campos son válidos, disparar el spinner y hacer el submit
      const loadingOverlay = document.querySelector('[data-loading-target="overlay"]')
      if (loadingOverlay) {
        loadingOverlay.classList.remove('d-none')
      }
      // Hacer el submit del formulario manualmente
      form.requestSubmit()
    }
    return false
  }
}
